import { useFlowSessionById } from "@/api/flow_sessions";
import { useFlowMessages } from "@/api/messages";
import { FlowSession, Message, ToolCall } from "@cohesive/api-client";
import { ProcessStatus } from "@cohesive/ui-components";
import { useEffect, useMemo, useState } from "react";
import { titleCase } from "title-case";

/**
 * Extended process status that includes needs_attention state.
 * Maps to ProcessStatus but adds granularity for user action required.
 */
export type FlowSessionLiveStatus =
  | "pending"
  | "processing"
  | "needs_attention"
  | "complete"
  | "error";

export interface FlowSessionLiveStatusResult {
  /** Current status of the flow session */
  status: FlowSessionLiveStatus;
  /** Status mapped to ProcessStatus for UI components */
  processStatus: ProcessStatus;
  /** Description of current activity */
  currentActivity?: string;
  /** Whether data is still loading */
  isLoading: boolean;
  /** The flow session data */
  flowSession?: FlowSession;
  /** Tool calls that require user confirmation */
  pendingToolCalls?: ToolCall[];
}

/**
 * Extracts all tool calls from flow messages
 */
function extractToolCalls(messages: Message[]): ToolCall[] {
  const toolCalls: ToolCall[] = [];
  for (const message of messages) {
    if (message.tool_calls && message.tool_calls.length > 0) {
      toolCalls.push(...message.tool_calls);
    }
  }
  return toolCalls;
}

/**
 * Checks if any tool call has a pending confirmation status
 */
function hasPendingToolConfirmation(toolCalls: ToolCall[]): boolean {
  return toolCalls.some((tc) => tc.user_confirmation_status === "pending");
}

/**
 * Checks if any tool call is currently being processed after confirmation
 */
function hasProcessingToolConfirmation(toolCalls: ToolCall[]): boolean {
  return toolCalls.some(
    (tc) =>
      tc.user_confirmation_status === "confirmed" ||
      tc.user_confirmation_status === "confirmed-processing",
  );
}

/**
 * Gets the pending tool calls that need user confirmation
 */
function getPendingToolCalls(toolCalls: ToolCall[]): ToolCall[] {
  return toolCalls.filter((tc) => tc.user_confirmation_status === "pending");
}

/**
 * Checks if any tool call is currently processing
 */
function hasProcessingToolCall(toolCalls: ToolCall[]): boolean {
  return toolCalls.some(
    (tc) => tc.status === "processing" && !tc.user_confirmation_status,
  );
}

/**
 * Maps FlowSessionLiveStatus to ProcessStatus for UI components
 */
function mapToProcessStatus(status: FlowSessionLiveStatus): ProcessStatus {
  switch (status) {
    case "pending":
      return "pending";
    case "processing":
      return "processing";
    case "needs_attention":
      return "needs_attention";
    case "complete":
      return "complete";
    case "error":
      return "error";
  }
}

/**
 * Hook to get live status updates for any flow session.
 * Determines the current status based on flow session state and tool call confirmations.
 * Can be used for any process that has a flow session.
 */
export function useFlowSessionLiveStatus(
  flowSessionId: string | undefined,
): FlowSessionLiveStatusResult {
  // Query the flow session using existing hook
  const { data: flowSession, isLoading: isSessionLoading } = useFlowSessionById(
    flowSessionId ?? "",
    { enabled: !!flowSessionId },
  );

  // Query flow messages using existing hook to check tool call statuses
  const { data: flowMessages, isLoading: isMessagesLoading } = useFlowMessages(
    flowSessionId ?? "",
    undefined,
    { enabled: !!flowSessionId },
  );

  // Extract tool calls from messages
  const toolCalls = useMemo(() => {
    return flowMessages ? extractToolCalls(flowMessages) : [];
  }, [flowMessages]);

  // Get pending tool calls
  const pendingToolCalls = useMemo(() => {
    return getPendingToolCalls(toolCalls);
  }, [toolCalls]);

  // Derive status from flow session and tool call states
  const status = useMemo((): FlowSessionLiveStatus => {
    if (
      flowSession?.status === "ready" &&
      !hasPendingToolConfirmation(toolCalls) &&
      !hasProcessingToolConfirmation(toolCalls) &&
      !hasProcessingToolCall(toolCalls)
    ) {
      return "complete";
    }

    if (!flowSession) {
      return "pending";
    }

    // Check tool calls for pending confirmations - needs user action
    if (hasPendingToolConfirmation(toolCalls)) {
      return "needs_attention";
    }

    // Check if any tool call is being processed after confirmation
    if (
      hasProcessingToolConfirmation(toolCalls) ||
      hasProcessingToolCall(toolCalls)
    ) {
      return "processing";
    }

    // Map flow session status to live status
    // Flow session statuses: "enabled" | "deleted" | "ready" | "initialized" | "processing"
    const sessionStatus = flowSession.status as string;
    switch (sessionStatus) {
      case "initialized":
      case "processing":
        return "processing";
      case "ready":
      case "enabled":
        return "complete";
      case "deleted":
        return "error";
      default:
        // Check if it looks like a terminal state
        if (
          sessionStatus?.includes("done") ||
          sessionStatus?.includes("complete")
        ) {
          return "complete";
        }
        if (
          sessionStatus?.includes("fail") ||
          sessionStatus?.includes("cancel")
        ) {
          return "error";
        }
        return "processing";
    }
  }, [flowSession, toolCalls]);

  // Derive current activity text from the session state and tool calls
  const currentActivity = useMemo((): string | undefined => {
    if (!flowSession) {
      return undefined;
    }

    // If there are pending tool confirmations, show that
    if (hasPendingToolConfirmation(toolCalls)) {
      const pendingCall = pendingToolCalls[0];
      if (pendingCall?.function?.arguments?.action_description) {
        return `Awaiting approval: ${titleCase(
          pendingCall.function.arguments?.action_description,
          {
            sentenceCase: true,
          },
        )}`;
      }
      return "Waiting for your approval";
    }

    // If a tool is being processed after confirmation
    if (
      hasProcessingToolConfirmation(toolCalls) ||
      hasProcessingToolCall(toolCalls)
    ) {
      const processingCall = toolCalls.findLast(
        (tc) =>
          tc.status === "processing" ||
          tc.user_confirmation_status === "confirmed" ||
          tc.user_confirmation_status === "confirmed-processing",
      );
      if (processingCall?.function?.arguments?.action_description) {
        return (
          titleCase(processingCall.function.arguments?.action_description, {
            sentenceCase: true,
          }) + "..."
        );
      }
      return "Executing action...";
    }

    // Use session name if available as activity indicator
    if (flowSession.name && flowSession.status === "processing") {
      return flowSession.name;
    }

    return undefined;
  }, [flowSession, toolCalls, pendingToolCalls]);

  // Persist currentActivity to avoid flashing back to undefined between tool calls
  const [persistedActivity, setPersistedActivity] = useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    if (currentActivity && currentActivity !== persistedActivity) {
      setPersistedActivity(currentActivity);
    }
  }, [currentActivity, status]);

  return {
    status,
    processStatus: mapToProcessStatus(status),
    currentActivity: persistedActivity,
    isLoading: isSessionLoading || isMessagesLoading,
    flowSession: flowSession ?? undefined,
    pendingToolCalls:
      pendingToolCalls.length > 0 ? pendingToolCalls : undefined,
  };
}
